import { AxiosResponse } from "axios";

import { APIListValuesResponse, EntityAttribute } from "shared/api/api";
import client from "shared/api/axios";
import { VALUES_ENDPOINT_LIMIT_DEFAULT } from "shared/schemas/constants";
import { M2MRelationshipsGroupBy } from "shared/schemas/types";
import { constructValuesEndpoint } from "shared/schemas/utils/schema-utils";

import { ContextName, GroupByRelationIDValues } from "./types";

export const getAllGroupByRelationIDValues = async (
  attributesByContext: Record<ContextName, EntityAttribute[]>
): Promise<GroupByRelationIDValues> => {
  const promises = Object.entries(attributesByContext).map(
    async ([context, attributes]) => {
      const values = await getGroupByRelationIDValues(attributes);
      const contextName = context as ContextName;
      if (!values)
        return { context: contextName, values: {} as M2MRelationshipsGroupBy };

      return { context: contextName, values };
    }
  );
  const results = await Promise.all(promises);

  return results.reduce((acc, cur) => {
    acc[cur.context] = cur.values;

    return acc;
  }, {} as GroupByRelationIDValues);
};

const getGroupByRelationIDValues = async (
  attributes: EntityAttribute[] | undefined
) => {
  const valuesEndpoints = attributes
    ?.filter(
      (a) =>
        a.relationEndpoint && a.relationEndpointIDColumn && a.attributeGrouping
    )
    .map((a) => ({
      endpoint: constructValuesEndpoint(
        a.relationEndpoint!,
        a.relationEndpointIDColumn!
      ),
      originalEndpoint: a.relationEndpoint!,
    }));

  if (!valuesEndpoints) return;

  const responses: AxiosResponse<APIListValuesResponse>[] | void =
    await Promise.all(
      valuesEndpoints.map(({ endpoint }) =>
        client.get<APIListValuesResponse>(endpoint, {
          params: { limit: VALUES_ENDPOINT_LIMIT_DEFAULT },
        })
      )
    ).catch((error) => {
      console.error("Error fetching relationship values:", error);
    });

  if (!responses || responses.length === 0) return {};

  const newVals: M2MRelationshipsGroupBy = Object.fromEntries(
    responses.map(({ data }, i) => [
      valuesEndpoints[i].originalEndpoint,
      data.distinctValues.filter((x) => x !== null),
    ])
  );

  return newVals;
};
